// Bibliothèque « Marketing Studio » — un modèle de prompt par format de pub
// (UGC, unboxing, try-on, crush test, TV spot…). Un préréglage ne fait que
// composer un texte : c'est le modèle vidéo choisi dans le Studio qui génère,
// donc aucun crédit n'est débité par le préréglage lui-même.
//
// Chaque préréglage expose :
//   scene(ctx)  → la scène, alimentée par le produit / l'avatar / le décor
//   camera      → cadrage, style d'image, rythme et durée
//   capability  → l'outil du Studio le plus adapté (voir CAPABILITY_LABELS)
//   aspect      → format conseillé, appliqué au composeur
//   motion      → mouvement caméra conseillé (clés de MOTION_PRESETS)

export const PRESET_CATEGORIES = ["Tous", "TikTok", "UGC", "Commercial"];

// Couche « plateforme » : le même format n'est pas monté pareil selon la régie.
// `aspects` sert à résoudre le ratio (on garde celui du format s'il est permis,
// sinon on prend le premier), `directive` s'ajoute au prompt, `close` décrit la
// fin de plan attendue par la régie.
export const PLATFORMS = [
  {
    key: "tiktok",
    label: "TikTok",
    blurb: "UGC natif, hook 1-2 s",
    aspects: ["9:16"],
    directive:
      "Codes TikTok : image verticale plein écran filmée au téléphone, aucun aspect « pub », " +
      "hook visuel et parlé dès la première seconde, coupes toutes les 1 à 2 secondes, " +
      "sous-titres animés mot à mot, son direct et voix naturelle, rythme calé sur un son tendance.",
    close: "Dernier plan sur le produit en main avec le prix affiché en gros.",
  },
  {
    key: "snapchat",
    label: "Snapchat",
    blurb: "Gen-Z, néon, coupes très rapides",
    aspects: ["9:16"],
    directive:
      "Codes Snapchat : vertical plein écran, énergie Gen-Z assumée, gros caractères néon qui " +
      "apparaissent en rythme, coupes très rapides et punchy, couleurs saturées, " +
      "première image ultra contrastée pour arrêter le pouce, pensé son allumé.",
    close: "Dernier plan avec une flèche animée vers le haut et la mention « Swipe up ».",
  },
  {
    key: "meta",
    label: "Meta (Facebook / Instagram)",
    blurb: "Conversion, lisible son coupé",
    aspects: ["4:5", "9:16"],
    directive:
      "Codes Meta : cadrage stable façon démonstration produit, bénéfice énoncé explicitement, " +
      "le message doit passer son coupé (tout ce qui est dit est aussi écrit à l'écran), " +
      "produit visible dans les 2 premières secondes, lumière propre et rassurante.",
    close: "Carte de fin nette : produit détouré, offre et bouton d'appel à l'action.",
  },
  {
    key: "google",
    label: "Google (YouTube Shorts / Demand Gen)",
    blurb: "Voix off claire, intention de recherche",
    aspects: ["9:16", "16:9"],
    directive:
      "Codes Google : le besoin auquel répond le produit est nommé dans les 3 premières secondes, " +
      "voix off claire et posée, produit montré en gros plan net à plusieurs reprises, " +
      "aucune promesse exagérée ni texte clignotant, sous-titres incrustés propres.",
    close: "Carte de fin sobre : logo de la marque, nom du produit et appel à l'action.",
  },
];

export const findPlatform = (key) => PLATFORMS.find((p) => p.key === key) || null;

// Ratio effectif : le format garde le sien si la régie l'accepte (TV Spot reste
// en 16:9 sur Google), sinon on prend le premier ratio de la régie.
export const resolveAspect = (preset, platform) => {
  if (!platform) return preset.aspect;
  return platform.aspects.includes(preset.aspect) ? preset.aspect : platform.aspects[0];
};

// Valeurs de repli : un préréglage reste utilisable sans rien remplir.
export const PRESET_DEFAULTS = {
  product: "le produit",
  avatar: "une créatrice de contenu de 25 ans, look naturel, tenue simple",
  setting: "un intérieur lumineux et épuré",
  hook: "",
  cta: "",
  brandColors: "",
};

export const MARKETING_PRESETS = [
  {
    key: "ugc",
    label: "UGC",
    blurb: "Vidéos réseaux sociaux réalistes",
    tags: ["UGC", "TikTok"],
    capability: "ugc",
    aspect: "9:16",
    motion: "handheld",
    scene: (c) =>
      `${c.avatar} se filme en selfie dans ${c.setting} et parle spontanément de ${c.product} : ` +
      `elle le présente face caméra, l'utilise vraiment une seconde, puis hoche la tête, convaincue.`,
    camera:
      "Style UGC natif : caméra frontale de téléphone tenue à bout de bras, léger tremblement, " +
      "lumière naturelle de fenêtre, son direct, sous-titres animés, hook dans les 2 premières secondes, 8 secondes.",
  },
  {
    key: "gadget-saved-me",
    label: "This Gadget Saved Me",
    blurb: "Les atouts produit en recommandation de créateur",
    tags: ["UGC", "TikTok"],
    capability: "ugc",
    aspect: "9:16",
    motion: "handheld",
    scene: (c) =>
      `${c.avatar} bute sur un problème agaçant du quotidien dans ${c.setting}, souffle, ` +
      `puis attrape ${c.product} et règle le problème sous nos yeux. Visage soulagé, ` +
      `regard complice vers la caméra comme un conseil entre amis.`,
    camera:
      "Montage en trois temps (problème → produit → soulagement), plans serrés sur les mains, " +
      "coupes sèches, style créateur authentique, sous-titres animés, 8 secondes.",
  },
  {
    key: "giant-figure",
    label: "Giant Figure",
    blurb: "Le produit surdimensionné, impossible à scroller",
    tags: ["Commercial", "TikTok"],
    capability: "image2video",
    aspect: "9:16",
    motion: "crane-up",
    scene: (c) =>
      `${c.product} apparaît en version géante, haute comme un immeuble, posée au milieu de ${c.setting}. ` +
      `Des passants minuscules s'arrêtent et lèvent la tête, un souffle de vent soulève la poussière autour de la base.`,
    camera:
      "Plan large cinématographique en contre-plongée, échelle exagérée, lumière de fin de journée, " +
      "rendu photoréaliste haut de gamme, mouvement de grue lent qui révèle l'objet, 8 secondes.",
  },
  {
    key: "unboxing-tryon",
    label: "Unboxing Virtual Try-On",
    blurb: "Déballage et essayage en une seule prise",
    tags: ["UGC", "TikTok"],
    capability: "image2video",
    aspect: "9:16",
    motion: "handheld",
    scene: (c) =>
      `${c.avatar} ouvre le colis de ${c.product} dans ${c.setting}, écarte le papier de soie, ` +
      `sort le produit, l'enfile ou l'installe immédiatement puis se tourne vers le miroir, ` +
      `visiblement contente du rendu — tout en un seul plan continu.`,
    camera:
      "Plan-séquence à hauteur de poitrine, caméra à l'épaule qui suit les mains puis remonte au miroir, " +
      "lumière douce, ambiance sonore réelle (papier, fermeture), 8 secondes.",
  },
  {
    key: "unboxing-asmr",
    label: "Unboxing ASMR",
    blurb: "Déballages ASMR ultra satisfaisants",
    tags: ["UGC", "TikTok"],
    capability: "ugc",
    aspect: "9:16",
    motion: "static",
    scene: (c) =>
      `Gros plan sur des mains soignées qui déballent ${c.product} très lentement sur une surface claire : ` +
      `ongle qui décolle l'adhésif, carton qui craque, papier de soie froissé, produit qui glisse hors de son écrin, ` +
      `puis doigt qui caresse la surface.`,
    camera:
      "Macro sur trépied, fond neutre, lumière rasante qui révèle les textures, ralenti léger, " +
      "audio ASMR très présent, aucun dialogue, 8 secondes.",
  },
  {
    key: "tryon-sneakers",
    label: "Virtual Try-On Sneakers",
    blurb: "Essayage virtuel de sneakers",
    tags: ["UGC", "TikTok"],
    capability: "image2video",
    aspect: "9:16",
    motion: "pan-right",
    scene: (c) =>
      `${c.avatar}, cadrée aux pieds dans ${c.setting} : elle enfile ${c.product}, serre les lacets, ` +
      `tape deux fois du talon puis fait un pas de côté; la caméra remonte le long de la jambe jusqu'au regard satisfait.`,
    camera:
      "Départ au sol en plan très serré, remontée fluide de la caméra, lumière contrastée façon campagne sportswear, " +
      "coupe rythmée sur le tapement de talon, 8 secondes.",
  },
  {
    key: "couple-home",
    label: "Couple Sharing At Home",
    blurb: "Un couple qui partage le produit à la maison",
    tags: ["UGC", "Commercial"],
    capability: "ugc",
    aspect: "9:16",
    motion: "handheld",
    scene: (c) =>
      `Un couple de trentenaires, détendu dans ${c.setting}, découvre ${c.product} ensemble : ` +
      `l'un le tend à l'autre, ils l'essaient à deux, rient d'un détail, puis se regardent d'un air « on le garde ».`,
    camera:
      "Caméra à l'épaule discrète, cadrage à deux, lumière chaude de fin d'après-midi, " +
      "dialogue court et naturel, sous-titres, 8 secondes.",
  },
  {
    key: "selfie-testimonial",
    label: "Selfie Testimonial",
    blurb: "Témoignages selfie authentiques",
    tags: ["UGC", "TikTok"],
    capability: "ugc",
    aspect: "9:16",
    motion: "handheld",
    scene: (c) =>
      `${c.avatar}, bras tendu, raconte en selfie son avant/après avec ${c.product} dans ${c.setting} : ` +
      `elle démarre par la phrase choc, montre le produit à l'écran une seconde, puis termine sur un sourire franc.`,
    camera:
      "Caméra frontale de téléphone, visage cadré large, léger recadrage pendant qu'elle parle, " +
      "lumière naturelle, son direct un peu imparfait, sous-titres animés, 8 secondes.",
  },
  {
    key: "direct-to-camera",
    label: "Direct-to-Camera",
    blurb: "Créateur qui s'adresse droit à la caméra",
    tags: ["UGC", "Commercial"],
    capability: "avatar",
    aspect: "9:16",
    motion: "static",
    scene: (c) =>
      `${c.avatar} s'adresse directement à la caméra dans ${c.setting}, ${c.product} bien visible en main : ` +
      `elle énonce le bénéfice principal, marque une pause, puis conclut en pointant le produit vers l'objectif.`,
    camera:
      "Plan poitrine centré, caméra fixe sur trépied, arrière-plan légèrement flou, lumière douce frontale, " +
      "élocution claire, sous-titres, 8 secondes.",
  },
  {
    key: "secret-hack",
    label: "Secret Hack Reveal",
    blurb: "Révélation d'une astuce produit maline",
    tags: ["UGC", "TikTok"],
    capability: "ugc",
    aspect: "9:16",
    motion: "zoom-in",
    scene: (c) =>
      `${c.avatar} regarde autour d'elle comme pour vérifier que personne n'écoute, se penche vers la caméra ` +
      `et révèle l'astuce que personne n'utilise avec ${c.product}, puis la démontre immédiatement dans ${c.setting} — ` +
      `résultat surprenant, sourcils levés.`,
    camera:
      "Zoom avant progressif pendant la confidence, plan serré, lumière basse et intime, " +
      "coupe nette sur la démonstration, sous-titres appuyés, 8 secondes.",
  },
  {
    key: "crush-test",
    label: "Crush Test",
    blurb: "Tests de résistance satisfaisants",
    tags: ["Commercial", "TikTok"],
    capability: "text2video",
    aspect: "9:16",
    motion: "static",
    scene: (c) =>
      `Au centre d'un plateau propre, on installe ${c.product}; une presse hydraulique descend lentement, ` +
      `le produit résiste, la surface se déforme puis revient en place — la caméra reste sur l'objet intact.`,
    camera:
      "Plan macro fixe, fond sombre, éclairage industriel dur, ralenti extrême sur l'instant de contact, " +
      "son mécanique lourd, aucune voix, 8 secondes.",
  },
  {
    key: "hyper-motion",
    label: "Hyper Motion",
    blurb: "Salves de mouvement produit haute énergie",
    tags: ["Commercial", "TikTok"],
    capability: "text2video",
    aspect: "9:16",
    motion: "orbit",
    scene: (c) =>
      `Une succession de décors se recompose autour de ${c.product} : le produit tourne sur lui-même, ` +
      `explose en particules lumineuses puis se réassemble, parfaitement net, ` +
      `sur un fond aux couleurs saturées.`,
    camera:
      "Orbite rapide, coupes toutes les 15 images, motion blur assumé, flashs lumineux synchronisés au beat, " +
      "rendu 3D publicitaire très propre, 8 secondes.",
  },
  {
    key: "camera-pov",
    label: "Camera POV",
    blurb: "Moments produit en vue subjective",
    tags: ["UGC", "TikTok"],
    capability: "text2video",
    aspect: "9:16",
    motion: "handheld",
    scene: (c) =>
      `Vue subjective à la première personne : nos propres mains attrapent ${c.product} dans ${c.setting}, ` +
      `l'inspectent, l'utilisent, puis le reposent sur la table — comme si le spectateur vivait la scène.`,
    camera:
      "Caméra portée au niveau des yeux, grand angle léger, respiration visible dans le cadre, " +
      "lumière ambiante réelle, son immersif, 8 secondes.",
  },
  {
    key: "classic-modern",
    label: "Classic Meets Modern",
    blurb: "Mélange d'intemporel et de contemporain",
    tags: ["Commercial"],
    capability: "text2video",
    aspect: "9:16",
    motion: "pan-left",
    scene: (c) =>
      `On présente ${c.product} dans un décor patrimonial — marbre, bois sculpté, lumière de vitrail — ` +
      `puis un panoramique révèle la même scène en version contemporaine, béton clair et néon, ` +
      `le produit restant identique au centre.`,
    camera:
      "Panoramique latéral continu qui fait basculer les deux époques, plan sur rail, " +
      "étalonnage chaud puis froid, musique orchestrale qui vire électronique, 8 secondes.",
  },
  {
    key: "mess-to-fresh",
    label: "Mess to Fresh",
    blurb: "Transformation du désordre au impeccable",
    tags: ["Commercial", "TikTok"],
    capability: "text2video",
    aspect: "9:16",
    motion: "static",
    scene: (c) =>
      `${c.setting} est en désordre complet, lumière grise et terne; ${c.product} entre dans le cadre, ` +
      `un balayage lumineux traverse la scène et tout redevient net, rangé et éclatant en un seul mouvement.`,
    camera:
      "Plan fixe identique avant/après pour rendre le contraste évident, transition par balayage, " +
      "étalonnage terne → lumineux, whoosh sonore net, 8 secondes.",
  },
  {
    key: "mystery-box",
    label: "Mystery Box",
    blurb: "Révélations de boîte mystère à suspense",
    tags: ["UGC", "TikTok"],
    capability: "ugc",
    aspect: "9:16",
    motion: "zoom-in",
    scene: (c) =>
      `Une boîte noire non identifiée est posée devant ${c.avatar} dans ${c.setting}; elle hésite, ` +
      `secoue la boîte contre son oreille, soulève le couvercle très lentement — une lueur s'échappe — ` +
      `et ${c.product} apparaît enfin, sous son regard stupéfait.`,
    camera:
      "Plan serré sur la boîte, zoom avant lent pour tenir le suspense, lumière dirigée sur l'ouverture, " +
      "silence puis révélation sonore, 8 secondes.",
  },
  {
    key: "reboxing",
    label: "Reboxing",
    blurb: "Remise en boîte et re-révélation",
    tags: ["Commercial", "TikTok"],
    capability: "text2video",
    aspect: "9:16",
    motion: "zoom-out",
    scene: (c) =>
      `Déballage inversé : ${c.product} regagne tout seul son écrin, le papier de soie se replie, ` +
      `les rabats du carton se referment et l'étiquette se recolle parfaitement — un déballage joué à l'envers.`,
    camera:
      "Plan macro sur fond neutre, mouvement inversé fluide, lumière studio douce, " +
      "sons de papier lus à l'envers, zoom arrière final sur le colis intact, 8 secondes.",
  },
  {
    key: "tv-spot",
    label: "TV Spot",
    blurb: "Des histoires vraies, format grand écran",
    tags: ["Commercial"],
    capability: "text2video",
    aspect: "16:9",
    motion: "crane-up",
    scene: (c) =>
      `Enchaînement façon publicité télévisée : un visage sincère dans ${c.setting}, ` +
      `un geste précis avec ${c.product}, un plan large qui installe le décor, ` +
      `puis retour serré sur le produit posé, net et désirable.`,
    camera:
      "Trois plans cinématographiques enchaînés, caméra sur grue puis focale longue, " +
      "étalonnage chaud haut de gamme, musique montante, carte de fin propre, 8 secondes.",
  },
  {
    key: "ugc-addiction",
    label: "UGC Addiction",
    blurb: "L'obsession produit qu'on ne lâche plus",
    tags: ["UGC", "TikTok"],
    capability: "ugc",
    aspect: "9:16",
    motion: "handheld",
    scene: (c) =>
      `${c.avatar} n'arrive plus à lâcher ${c.product} : elle l'utilise au réveil, dans ${c.setting}, ` +
      `puis en pleine soirée — trois moments enchaînés, même geste répété, sourire de plus en plus large, ` +
      `elle hausse les épaules face caméra comme pour s'excuser.`,
    camera:
      "Trois vignettes UGC très courtes montées cut, caméra frontale de téléphone, " +
      "lumières différentes à chaque plan, sous-titres animés, 8 secondes.",
  },
  {
    key: "before-after",
    label: "Before and After",
    blurb: "Transformations et résultats mis en avant",
    tags: ["Commercial", "UGC"],
    capability: "text2video",
    aspect: "9:16",
    motion: "static",
    scene: (c) =>
      `Écran partagé : à gauche la situation avant, frustrante et terne dans ${c.setting}; ` +
      `à droite la même scène après ${c.product}, nette et lumineuse. ` +
      `La séparation glisse vers la gauche jusqu'à ne laisser que le résultat.`,
    camera:
      "Split-screen aligné au pixel, même cadrage des deux côtés, transition par volet coulissant, " +
      "étalonnage froid → chaud, 8 secondes.",
  },
  {
    key: "tutorial",
    label: "Tutorial",
    blurb: "Tutoriels pas à pas",
    tags: ["Commercial", "UGC"],
    capability: "ugc",
    aspect: "9:16",
    motion: "static",
    scene: (c) =>
      `${c.avatar} explique ${c.product} en trois étapes numérotées dans ${c.setting} : ` +
      `étape 1 elle prépare, étape 2 elle applique le geste clé, étape 3 elle montre le résultat en gros plan.`,
    camera:
      "Vue en plongée sur le plan de travail alternée avec des plans serrés sur les mains, caméra fixe, " +
      "lumière neutre, numéros d'étape à l'écran, sous-titres, 8 secondes.",
  },
  {
    key: "unboxing",
    label: "Unboxing",
    blurb: "Déballage haute qualité",
    tags: ["Commercial", "UGC"],
    capability: "ugc",
    aspect: "9:16",
    motion: "zoom-in",
    scene: (c) =>
      `Déballage soigné de ${c.product} sur une table claire : le colis est ouvert au cutter, ` +
      `les rabats s'écartent, le produit est soulevé à deux mains et présenté face caméra, ` +
      `puis reposé à côté de son emballage.`,
    camera:
      "Plans serrés sur les mains, zoom avant lent, lumière studio douce et rebond blanc, " +
      "rendu premium très net, sons de matière, 8 secondes.",
  },
  {
    key: "ugc-tryon",
    label: "UGC Virtual Try On",
    blurb: "Essayer avant d'acheter",
    tags: ["UGC", "TikTok"],
    capability: "image2video",
    aspect: "9:16",
    motion: "handheld",
    scene: (c) =>
      `${c.avatar} se filme devant un miroir dans ${c.setting}, essaie ${c.product}, ` +
      `pivote d'un quart de tour pour montrer les deux profils, ajuste un détail puis lève le pouce à la caméra.`,
    camera:
      "Caméra de téléphone tenue à la main face au miroir, cadrage plein pied, lumière naturelle, " +
      "rendu authentique non retouché, sous-titres, 8 secondes.",
  },
  {
    key: "pro-tryon",
    label: "Pro Virtual Try On",
    blurb: "Essayage virtuel avancé",
    tags: ["Commercial"],
    capability: "image2video",
    aspect: "9:16",
    motion: "orbit",
    scene: (c) =>
      `Séance photo professionnelle : un mannequin porte ${c.product} sur fond studio uni, ` +
      `il marque une pose, la caméra tourne autour de lui pour révéler la coupe et les matières sous tous les angles.`,
    camera:
      "Orbite lente à 180°, éclairage studio trois points, fond cyclorama, rendu mode haute couture, " +
      "netteté maximale sur les textures, 8 secondes.",
  },
  {
    key: "wild-card",
    label: "Wild Card",
    blurb: "Mode libre pour vos idées sur mesure",
    tags: ["Commercial", "TikTok", "UGC"],
    capability: "text2video",
    aspect: "9:16",
    motion: null,
    scene: (c) =>
      `Idée libre autour de ${c.product} dans ${c.setting} : une scène inattendue, un décalage visuel fort, ` +
      `un événement qui surprend dans les deux premières secondes et qui donne envie de regarder jusqu'au bout. ` +
      `(Réécrivez cette phrase avec votre propre concept.)`,
    camera:
      "Style libre, mais gardez un sujet clair, un événement marquant et une chute nette, 8 secondes.",
  },
];

export const findPreset = (key) => MARKETING_PRESETS.find((p) => p.key === key) || null;

export const presetsByCategory = (category) =>
  !category || category === "Tous"
    ? MARKETING_PRESETS
    : MARKETING_PRESETS.filter((p) => p.tags.includes(category));

// Compose le prompt final : scène du préréglage + format + directives caméra
// + codes de la régie choisie + textes à l'écran + palette de marque optionnelle.
// `platform` est une clé de PLATFORMS (ou rien pour un prompt neutre).
export const buildPresetPrompt = (preset, ctx = {}, platform = null) => {
  if (!preset) return "";
  const filled = Object.fromEntries(
    Object.entries(ctx).filter(([, v]) => typeof v === "string" && v.trim())
  );
  const c = { ...PRESET_DEFAULTS, ...filled };
  const plat = typeof platform === "string" ? findPlatform(platform) : platform;

  // Le produit de l'utilisateur peut ouvrir la phrase : on remet la majuscule.
  const scene = preset.scene(c);
  const parts = [
    scene.charAt(0).toUpperCase() + scene.slice(1),
    `Format ${resolveAspect(preset, plat)}.`,
    preset.camera,
  ];
  if (plat) parts.push(plat.directive);
  if (c.hook) parts.push(`Texte à l'écran : « ${c.hook} »${c.cta ? ` puis « ${c.cta} »` : ""}.`);
  else if (c.cta) parts.push(`Carte de CTA en fin de plan : « ${c.cta} ».`);
  if (plat) parts.push(plat.close);
  if (c.brandColors) parts.push(`Couleurs de marque : ${c.brandColors}.`);
  return parts.join(" ");
};
